import {DateUtils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isValid} from 'date-fns';
import React, {useCallback, useState} from 'react';
import {Placement} from 'react-bootstrap/esm/types';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig, useFormatters} from './LightFormatter';
import {FilterNodeTypeDateMonthRange} from 'src/js/containers/filtering';
import {DynamicFilter, FILTER_TYPE_DATE_MONTH_RANGE, MonthRangeFilter, ShortcutEnum} from 'src/js/lib/filtering';
import {FilterNode} from 'src/js/model/filters_config';

type Props = {
  config: FilterConfig;
  value: {startDate: string; endDate: string; shortcut?: ShortcutEnum | null} | null | undefined;
  defaultValue?: {startDate: string; endDate: string; shortcut?: ShortcutEnum | null} | null | undefined;
  onChange?: (newValue: {startDate: string; endDate: string; shortcut?: ShortcutEnum | null} | null) => void;
  isDefaultValue?: boolean;
  placement?: Placement;
  fullWidth?: boolean;
  isRemoveFilterButtonHidden?: boolean;
  showShortcuts?: boolean;
};
export const LightMonthRangeFilter: React.FC<Props> = (props) => {
  const {
    config: _config,
    value,
    defaultValue,
    onChange,
    isDefaultValue,
    placement = 'bottom-start',
    fullWidth = false,
    isRemoveFilterButtonHidden = false,
    showShortcuts,
  } = props;
  const filterType = FILTER_TYPE_DATE_MONTH_RANGE;
  const {t} = useFormatters();
  const config = {
    ..._config,
    ui: {
      label: t(_config.ui.label),
      placeholder: _config.ui.placeholder ? t(_config.ui.placeholder) : _config.ui.placeholder,
      type: filterType,
    },
  };
  const [isOpened, setIsOpened] = useState(false);
  const toggleOpened = useCallback(() => {
    setIsOpened((v) => !v);
  }, []);
  const MonthRangeFilter = DynamicFilter.getFilterClass(filterType);
  const filter = value
    ? new MonthRangeFilter({
        type: filterType,
        property: config.name,
        value: {
          startDate: isValid(new Date(value.startDate)) ? DateUtils.toMonthFormat(new Date(value.startDate)) : null,
          endDate: isValid(new Date(value.endDate)) ? DateUtils.toMonthFormat(new Date(value.endDate)) : null,
          shortcut: value.shortcut,
        },
      })
    : undefined;
  const changeFilter = useCallback(
    (newFilter: MonthRangeFilter) => {
      const startDate = newFilter.getStartDate();
      const endDate = newFilter.getEndDate();
      let shortcut: ShortcutEnum | null | undefined;
      if ('getShortcut' in newFilter) {
        shortcut = newFilter.getShortcut();
      }
      if (onChange)
        onChange({
          startDate: DateUtils.toIsoDateStringLocal(startDate),
          endDate: DateUtils.toIsoDateStringLocal(endDate),
          shortcut,
        });
    },
    [onChange]
  );
  const removeFilter = useCallback(
    (_newFilter: MonthRangeFilter) => {
      if (onChange) onChange(defaultValue ?? null);
    },
    [onChange, defaultValue]
  );
  const inputRef = useCallback(() => {
    // do nothing
  }, []);
  const setFocus = useCallback(() => 0, []);
  const node = new FilterNode(config);
  const isResetFiltersButtonDisabled =
    (value?.startDate === defaultValue?.startDate && value?.endDate === defaultValue?.endDate) ||
    value?.shortcut === defaultValue?.shortcut;
  return (
    <span className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')} data-name={config.name}>
      <FilterNodeTypeDateMonthRange
        node={node}
        isOpened={isOpened}
        toggleOpened={toggleOpened}
        changeFilter={changeFilter}
        removeFilter={removeFilter}
        selectedFilter={filter}
        inputRef={inputRef}
        setFocus={setFocus}
        isMui
        isDefaultValue={isDefaultValue}
        placement={placement}
        fullWidth={fullWidth}
        isRemoveFilterButtonHidden={isRemoveFilterButtonHidden}
        showShortcuts={showShortcuts}
        isResetFiltersButtonDisabled={isResetFiltersButtonDisabled}
      />
    </span>
  );
};
